'use client';

import { type FC, useEffect, useRef, useState } from 'react';
import type { Team } from '@cs-shot/battle-sim';
import { Spinner, EmptyState, Button, TabFilter, type TabFilterTab } from '../../ui';
import { FaGamepad, FiX, FaRegQuestionCircle } from '../../icons';
import { Credits } from '../../Credits';
import { ScreenContent, ScreenFixedTop } from '../../../screen';
import { RushMidTabsBar } from './shared/RushMidTabsBar';
import type { RushMidTab } from './useRushMidController';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './RushMid.translations';
import { useOpenGames, useLiveGames, useRushMidGamesSubscription, isJoinBlocked } from '../../../queries/useRushMidGames';
import { useSecondsUntil } from './openGameCountdown';
import type { OpenGame, LiveGame } from '../../../services/generated/Api';
import { modeLabel } from './HistoryScreen';
import { CardBackdrop, MiniSide } from './cardParts';
import { type OpenSeat } from './OpenGameScreen';
import { useBuildStale } from '../../BuildUpdateGuard';
import { LobbyUpdateNotice } from './LobbyUpdateNotice';
import { useRushMidConfig } from '../../../queries/useRushMidConfig';
import { FirstRunSlides } from './howto/FirstRunSlides';
import { HowToPlaySheet } from './howto/HowToPlaySheet';
import { useFirstRun } from './howto/useFirstRun';
import { loadBattleAssets } from './battleAssets';
import { PartySquadStrip } from './PartySquadStrip';
import { CreateGameSheet } from './CreateGameSheet';
import { useRushMidParty } from './RushMidPartyContext';
import { useScrollAnchor } from '../../../hooks/useScrollAnchor';

type Translate = ReturnType<typeof useLocalTranslation>['t'];

const INTRO_SEEN_KEY = 'rushmid:intro:seen';

/** Which of the two lists the home screen shows: open (FORMING) or live (PLAYING) games. */
type ListTab = 'open' | 'live';

/** The dismissable "what is Rush Mid" blurb on top of the home screen: shown until the
 *  user closes it, then never again on this device (localStorage, like the how-to gate). */
const useIntroCard = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(INTRO_SEEN_KEY)) setVisible(true);
    } catch { /* storage unavailable (private mode) — keep it hidden */ }
  }, []);
  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(INTRO_SEEN_KEY, '1'); } catch { /* same */ }
  };
  return { visible, dismiss };
};

/** Free slots on a side. A browsing party of k fits a side once it has ≥ k free slots. */
const freeSlots = (seats: readonly (OpenSeat | null)[]): number => seats.filter((s) => s === null).length;
const sideFitsParty = (seats: readonly (OpenSeat | null)[], k: number): boolean => freeSlots(seats) >= k;
const gameFitsParty = (game: OpenGame, k: number): boolean =>
  sideFitsParty(game.seats.t, k) || sideFitsParty(game.seats.ct, k);

/** «выигрыш до ×5» + the map name — the card's footer row over the map backdrop. */
const CardFooter: FC<{ ticket: number; map: string; t: Translate }> = ({ ticket, map, t }) => (
  <div className="flex items-center justify-between">
    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] backdrop-blur-md">
      {t('winUpToPrefix')} <Credits amount={ticket * 5} iconSize={12} /> {t('winUpToSuffix')}
    </span>
    <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] backdrop-blur-md">
      {map}
    </span>
  </div>
);

interface GameCardProps {
  game: OpenGame;
  /** absent for a party member browsing (rows go passive — only the leader picks). */
  onOpen?: (matchId: string) => void;
  /** the browsing leader's own party size (≥2) — dims cards where no side fits it. */
  partyK?: number;
  t: Translate;
}

export const GameCard: FC<GameCardProps> = ({ game, onOpen, partyK, t }) => {
  // The newbie grace fences this lobby off for us until `joinableAt`; the card says so and
  // stays untappable until then, instead of letting the join come back with an error.
  const graceLeft = useSecondsUntil(game.joinable === false ? game.joinableAt : undefined);
  const graceLocked = isJoinBlocked(game) && (graceLeft === null || graceLeft > 0);
  const partyWontFit = partyK !== undefined && !gameFitsParty(game, partyK);
  const dimmed = partyWontFit || graceLocked;
  const clickable = !!onOpen && !dimmed;
  const badge = graceLocked && graceLeft !== null
    ? t('graceLockedBadge', { s: graceLeft })
    : partyK !== undefined && partyWontFit
      ? t('partyWontFit', { k: partyK })
      : null;
  return (
    <li data-anchor-id={game.matchId}>
      <button
        type="button"
        onClick={() => { if (clickable) onOpen(game.matchId); }}
        className={`relative flex w-full flex-col gap-3 overflow-hidden rounded-2xl bg-bg-secondary p-4 text-left transition-opacity ${clickable ? 'active:opacity-80' : ''} ${dimmed ? 'opacity-40' : ''}`}
      >
        <CardBackdrop map={game.map} />
        <div className="relative flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-base font-extrabold text-white">
            <Credits amount={game.ticket} iconSize={15} /> · {modeLabel(game.mode, t)}
          </span>
          {badge && (
            <span className="rounded-full bg-black/50 px-2 py-0.5 text-[11px] font-semibold text-white/70">
              {badge}
            </span>
          )}
        </div>
        <div className="relative flex min-w-0 items-center justify-center gap-2">
          <MiniSide side="t" seats={game.seats.t} />
          <span className="shrink-0 text-xs font-bold text-white/70">VS</span>
          <MiniSide side="ct" seats={game.seats.ct} />
        </div>
        <div className="relative">
          <CardFooter ticket={game.ticket} map={game.map} t={t} />
        </div>
      </button>
    </li>
  );
};

/** A live (PLAYING) match card — the whole card is a «Смотреть» button. Mirrors the open
 *  card's seat strip; a «Идёт бой · раунд N» badge replaces the "created N ago" time. */
export const LiveGameCard: FC<{ game: LiveGame; onSpectate: (matchId: string) => void; t: Translate }> = ({ game, onSpectate, t }) => (
  <li data-anchor-id={game.matchId}>
    <button
      type="button"
      onClick={() => onSpectate(game.matchId)}
      className="relative flex w-full flex-col gap-3 overflow-hidden rounded-2xl bg-bg-secondary p-4 text-left transition-opacity active:opacity-80"
    >
      <CardBackdrop map={game.map} />
      <div className="relative flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-base font-extrabold text-white">
          <Credits amount={game.ticket} iconSize={15} /> · {modeLabel(game.mode, t)}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-red/20 px-2 py-0.5 text-[11px] font-semibold text-accent-red">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-red" />
          {t('liveRoundBadge', { n: game.round + 1 })}
        </span>
      </div>
      <div className="relative flex min-w-0 items-center justify-center gap-2">
        <MiniSide side="t" seats={game.seats.t} />
        <span className="shrink-0 text-xs font-bold text-white/70">VS</span>
        <MiniSide side="ct" seats={game.seats.ct} />
      </div>
      <div className="relative flex items-center justify-between">
        <span className="text-xs font-semibold text-accent-blue">{t('spectate')} →</span>
        <span className="rounded-full bg-black/50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white/70">
          {game.map}
        </span>
      </div>
    </button>
  </li>
);

export interface GamesHomeScreenProps {
  /** bypasses the query (Storybook/tests) */
  games?: OpenGame[];
  /** bypasses the live-games query (Storybook/tests) */
  liveGames?: LiveGame[];
  tab: RushMidTab;
  onTab: (tab: RushMidTab) => void;
  onOpen: (matchId: string) => void;
  onSpectate: (matchId: string) => void;
  onCreate: (mode: number, ticket: number, side: Team, matchMode: 'ranked' | 'bots') => void;
  onCreateParty: () => void;
  onJoinParty: (code: string) => void;
  /** Set (e.g. by the raffle tab's "Играть {ticket}") to pop the create sheet open on arrival —
   *  consumed once via onConsumePendingCreate, mirroring the ?game=/?party= deep-link pattern. */
  pendingCreate?: boolean;
  onConsumePendingCreate?: () => void;
  /** With pendingCreate — the entry tier to preselect (raffle «Играть {ticket}»). */
  createTicket?: number;
}

/**
 * The «Игры» tab is the app's home screen: the squad strip, a gated create CTA and the live
 * open-games list all in one scroll. CTA priority: stale build → maintenance →
 * party ready-state → create.
 */
export const GamesHomeScreen: FC<GamesHomeScreenProps> = ({
  games: gamesProp, liveGames: liveProp, tab, onTab, onOpen, onSpectate, onCreate, onCreateParty, onJoinParty,
  pendingCreate, onConsumePendingCreate, createTicket,
}) => {
  const { t } = useLocalTranslation(translations);
  const stale = useBuildStale();
  const { maintenance } = useRushMidConfig();
  const party = useRushMidParty();
  const firstRun = useFirstRun();
  const intro = useIntroCard();
  const [sheetOpen, setSheetOpen] = useState(false);
  // the «как играть» link is now the only entry to the rules sheet (the tabs-bar «?» is gone)
  const [howtoOpen, setHowtoOpen] = useState(false);
  const [listTab, setListTab] = useState<ListTab>('open');

  useRushMidGamesSubscription();
  const query = useOpenGames(gamesProp === undefined);
  const games = gamesProp ?? query.data?.games;
  const isLoading = gamesProp === undefined && query.isLoading;
  const liveQuery = useLiveGames(liveProp === undefined);
  const liveGames = liveProp ?? liveQuery.data?.games;
  const isLiveLoading = liveProp === undefined && liveQuery.isLoading;

  const listTabs: TabFilterTab<ListTab>[] = [
    { id: 'open', label: t('openGamesTitle') },
    { id: 'live', label: t('liveNowTitle') },
  ];

  // the cached list paints on switch, a fresh one lands behind it
  const selectListTab = (next: ListTab) => {
    setListTab(next);
    if (next === 'open') void query.refetch();
    else void liveQuery.refetch();
  };

  // Keep the viewport pinned as games appear/disappear (open + live) — no scroll jump. Works on
  // iOS too, where the native CSS `overflow-anchor` fallback does nothing (unsupported in WebKit).
  const scrollRef = useRef<HTMLDivElement>(null);
  const gamesSignature = [...(games ?? []), ...(liveGames ?? [])].map((g) => g.matchId).join(',');
  useScrollAnchor(scrollRef, gamesSignature);

  // preload battle sprites while the player browses the games list
  useEffect(() => { loadBattleAssets(); }, []);

  useEffect(() => {
    if (!pendingCreate) return;
    setSheetOpen(true);
    onConsumePendingCreate?.();
  }, [pendingCreate, onConsumePendingCreate]);

  const partyK = party && party.isLeader && party.members.length >= 2 ? party.members.length : undefined;
  const cardsPassive = !!party && !party.isLeader;

  const handleCreate = (mode: number, ticket: number, side: Team, matchMode: 'ranked' | 'bots') => {
    setSheetOpen(false);
    onCreate(mode, ticket, side, matchMode);
  };

  const cta = (() => {
    if (stale) return <LobbyUpdateNotice />;
    if (maintenance) {
      return (
        <div className="flex flex-col items-stretch gap-2">
          <div className="rounded-xl border border-gold/30 bg-gold/10 px-4 py-2 text-center text-sm font-semibold text-gold">
            {t('maintenanceBanner')}
          </div>
          <Button variant="primary" size="l" stretched disabled>{t('createGameShort')}</Button>
        </div>
      );
    }
    if (party && !party.isLeader) {
      const you = party.members.find((m) => m.userId === party.youUserId);
      const readyCount = party.members.filter((m) => m.ready).length;
      return (
        <div className="flex flex-col items-stretch gap-2">
          <div className="rounded-xl bg-white/5 px-4 py-2 text-center text-sm text-label-secondary">
            {t('leaderPicking')}
          </div>
          {you?.ready ? (
            <Button variant="secondary" size="l" stretched onClick={() => party.onReady(false)}>
              {t('partyReadyCount', { n: readyCount, m: party.members.length })}
            </Button>
          ) : (
            <Button variant="primary" size="l" stretched data-sound="cta" onClick={() => party.onReady(true)}>
              {t('partyImReady')}
            </Button>
          )}
        </div>
      );
    }
    if (party && party.isLeader) {
      const readyCount = party.members.filter((m) => m.ready).length;
      const allReady = readyCount === party.members.length;
      if (!allReady) {
        return (
          <Button variant="primary" size="l" stretched disabled>
            {t('partyReadyCount', { n: readyCount, m: party.members.length })}
          </Button>
        );
      }
    }
    return (
      <Button variant="primary" size="l" stretched data-sound="cta" onClick={() => setSheetOpen(true)}>
        <span className="inline-flex items-center gap-2"><FaGamepad size={20} /> {t('createGameShort')}</span>
      </Button>
    );
  })();

  return (
    <>
      <ScreenFixedTop title={t('lobbyTitle')}>
        <RushMidTabsBar activeTab={tab} onChange={onTab} />
      </ScreenFixedTop>
      <ScreenContent hideLegalSection scrollKey="rush-mid-games-home" scrollRef={scrollRef}>
        <div data-sound="ring">
          <div className="flex min-h-full flex-col gap-3 px-4 pt-3 pb-24">
            {intro.visible && (
              <div className="relative rounded-2xl bg-bg-secondary p-4 pr-10">
                <p className="text-[15px] leading-relaxed text-label-primary">{t('introCard')}</p>
                <button
                  type="button"
                  aria-label="✕"
                  onClick={intro.dismiss}
                  className="absolute right-3 top-3 flex size-7 items-center justify-center rounded-full text-label-secondary active:opacity-60"
                >
                  <FiX size={18} />
                </button>
              </div>
            )}
            <PartySquadStrip onCreateParty={onCreateParty} onJoinParty={onJoinParty} />
            {cta}
            <div className="mt-1 flex items-center gap-3 px-1">
              <button
                type="button"
                onClick={() => setHowtoOpen(true)}
                className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-accent-blue active:opacity-60"
              >
                {t('howToPlayLink')} <FaRegQuestionCircle size={15} />
              </button>
              <TabFilter tabs={listTabs} activeTab={listTab} onChange={selectListTab} className="min-w-0 flex-1 p-0" />
            </div>
            {listTab === 'open' ? (
              isLoading ? (
                <div className="flex min-h-40 items-center justify-center">
                  <Spinner size="l" />
                </div>
              ) : !games || games.length === 0 ? (
                <EmptyState className="mt-6" title={t('gamesEmptyTitle')} description={t('gamesEmpty')} />
              ) : (
                <ul className="flex flex-col gap-2">
                  {[...games].sort((a, b) => a.createdAt.localeCompare(b.createdAt)).map((game) => (
                    <GameCard key={game.matchId} game={game} onOpen={cardsPassive ? undefined : onOpen} partyK={partyK} t={t} />
                  ))}
                </ul>
              )
            ) : isLiveLoading ? (
              <div className="flex min-h-40 items-center justify-center">
                <Spinner size="l" />
              </div>
            ) : !liveGames || liveGames.length === 0 ? (
              <EmptyState className="mt-6" title={t('gamesEmptyTitle')} description={t('liveEmpty')} />
            ) : (
              <ul className="flex flex-col gap-2">
                {[...liveGames].reverse().map((game) => (
                  <LiveGameCard key={game.matchId} game={game} onSpectate={onSpectate} t={t} />
                ))}
              </ul>
            )}
          </div>
        </div>
      </ScreenContent>
      <CreateGameSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} onCreate={handleCreate} initialTicket={createTicket} />
      <FirstRunSlides isOpen={firstRun.open} onClose={firstRun.dismiss} onDone={firstRun.dismiss} />
      <HowToPlaySheet isOpen={howtoOpen} onClose={() => setHowtoOpen(false)} />
    </>
  );
};

export default GamesHomeScreen;
